import { useState, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { usePlaidLink } from 'react-plaid-link'
import {
  Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import {
  createUpdateLinkToken,
  getPlaidItems,
  previewPlaidTransactions,
  refreshPlaidBalances,
  resetPlaidCursor,
  syncPlaidAccount,
} from '../api'
import { ApiError } from '../api/client'
import type { PlaidItem, PlaidAccountInfo, PlaidPreviewResult, PlaidTransactionPreview } from '../types'

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Filler)

// ── Constants ──────────────────────────────────────────────────────────────────

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const today       = new Date()

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmt(amount: number | null | undefined): string {
  if (amount == null) return '—'
  const abs = Math.abs(amount)
  return '$' + abs.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function money(v: number) {
  return '$' + Math.abs(v).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function typeDot(type: string) {
  const map: Record<string, string> = {
    credit: '#ef4444', depository: '#22c55e', loan: '#f97316', investment: '#06b6d4',
  }
  const color = map[type.toLowerCase()] ?? '#94a3b8'
  return (
    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
  )
}

function effectiveAvailable(acc: PlaidAccountInfo): number | null {
  const t = acc.type.toLowerCase()
  if (t === 'credit' || t === 'loan') {
    if (acc.balanceLimit != null && acc.balanceCurrent != null)
      return Math.max(0, acc.balanceLimit - acc.balanceCurrent)
    return acc.balanceAvailable
  }
  return acc.balanceAvailable
}

// Build a 12-month window ending `offset` months before today
// Navigation arrows jump ±6 months at a time.
function buildChartWindow(offset: number): { year: number; month: number; label: string }[] {
  const result = []
  for (let i = 11; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - offset - i, 1)
    result.push({
      year:  d.getFullYear(),
      month: d.getMonth() + 1,
      label: MONTH_NAMES[d.getMonth()] + " '" + String(d.getFullYear()).slice(2),
    })
  }
  return result
}

// ── Chart options ──────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CHART_OPTS: any = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#1e293b', padding: 10, cornerRadius: 10,
      callbacks: {
        label: (ctx: { raw: unknown }) => `${ctx.raw as number} transactions`,
      },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { color: '#94a3b8', font: { size: 10 } } },
    y: {
      beginAtZero: true, grid: { color: 'rgba(148,163,184,0.15)' }, border: { display: false },
      ticks: {
        color: '#94a3b8', font: { size: 10 }, stepSize: 1,
        callback: (v: unknown) => Number.isInteger(v as number) ? v : '',
      },
    },
  },
}

// ── Types ──────────────────────────────────────────────────────────────────────

type SyncResult = { added: number; modified: number; removed: number; lastSyncedAt: string | null }

// ── PlaidPreviewModal ──────────────────────────────────────────────────────────

interface PlaidPreviewModalProps {
  itemId:  string
  preview: PlaidPreviewResult
  onClose: () => void
  onSaved: (result: SyncResult) => void
}

function PlaidPreviewModal({ itemId, preview, onClose, onSaved }: PlaidPreviewModalProps) {
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState<string | null>(null)

  // Sort newest-first so the user sees the most-recent transactions at the top
  const sortedAdded = [...preview.added].sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
  const newAdded    = sortedAdded.filter(t => !t.existsInDb)
  const alreadyHave = sortedAdded.filter(t => t.existsInDb)
  const hasChanges  = newAdded.length > 0 || preview.modifiedCount > 0 || preview.removedCount > 0

  const handleSave = async () => {
    setSaving(true); setError(null)
    try {
      const result = await syncPlaidAccount(itemId)
      onSaved(result)
    } catch {
      setError('Save failed. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const pill = (bg: string, color: string, label: string) => (
    <span style={{ display: 'inline-block', fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 5, background: bg, color }}>{label}</span>
  )

  const renderRow = (tx: PlaidTransactionPreview, badge: ReactNode, key: string) => (
    <tr key={key} style={{ opacity: tx.existsInDb ? 0.45 : 1, borderBottom: '1px solid var(--pb-border)' }}>
      <td style={{ padding: '0.55rem 0.75rem', whiteSpace: 'nowrap', width: 100, fontSize: '0.82rem', color: 'var(--pb-muted)', verticalAlign: 'middle' }}>
        {tx.date ? new Date(tx.date + 'T00:00:00').toLocaleDateString() : '—'}
      </td>
      <td style={{ padding: '0.55rem 0.75rem', verticalAlign: 'middle' }}>
        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--pb-text)' }}>{tx.merchantDisplayName || tx.description || '—'}</div>
        {tx.description && tx.merchantDisplayName && tx.description !== tx.merchantDisplayName && (
          <div style={{ fontSize: '0.75rem', color: 'var(--pb-muted)' }}>{tx.description}</div>
        )}
      </td>
      <td style={{ padding: '0.55rem 0.75rem', width: 160, verticalAlign: 'middle' }}>
        {tx.category ? (
          <>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--pb-text)' }}>{tx.category}</div>
            {tx.subCategory && <div style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{tx.subCategory}</div>}
          </>
        ) : <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>—</span>}
      </td>
      <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right', width: 120, fontVariantNumeric: 'tabular-nums', fontWeight: 600, verticalAlign: 'middle' }}>
        <span style={{ color: tx.amount != null && tx.amount < 0 ? '#16a34a' : 'var(--pb-text)' }}>
          {tx.amount != null ? fmt(tx.amount) : '—'}
        </span>{' '}
        <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{tx.currencyCode}</span>
      </td>
      <td style={{ padding: '0.55rem 0.75rem', width: 130, verticalAlign: 'middle' }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {tx.isPending && pill('rgba(245,158,11,0.15)', '#b45309', 'Pending')}
          {badge}
        </div>
      </td>
    </tr>
  )

  return (
    <>
      {/* Solid backdrop — same as Matching Transactions modal */}
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={onClose} />
      <div
        className="modal fade show"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 60, paddingBottom: 24, zIndex: 1050 }}
        onClick={e => e.target === e.currentTarget && onClose()}
      >
        <div style={{ margin: 0, width: '95%', maxWidth: 1000, maxHeight: 'calc(100vh - 60px - 48px)', display: 'flex', flexDirection: 'column' }}>
          <div style={{
            background: 'var(--pb-glass-bg-strong)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
            color: 'var(--pb-text)', border: '1px solid var(--pb-border)',
            boxShadow: 'var(--pb-glass-shadow)',
            borderRadius: 16, display: 'flex', flexDirection: 'column',
            overflow: 'hidden', maxHeight: 'calc(100vh - 60px - 48px)',
          }}>

            {/* Header */}
            <div style={{ padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--pb-border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexShrink: 0, borderRadius: '16px 16px 0 0' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--pb-text)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className="bi bi-cloud-download" style={{ color: 'var(--pb-primary)' }} />
                  Transaction Preview
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--pb-muted)', marginTop: '0.2rem' }}>Review transactions before saving to the database.</div>
              </div>
              <button
                onClick={onClose}
                disabled={saving}
                className="btn-close"
              />
            </div>

            {/* Stats bar */}
            <div style={{ padding: '0.55rem 1.5rem', borderBottom: '1px solid var(--pb-border)', background: 'var(--pb-glass-bg-strong)', display: 'flex', gap: '1.25rem', flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--pb-text)' }}>
                {pill('rgba(34,197,94,0.15)', '#16a34a', String(newAdded.length))} new to import
              </span>
              {alreadyHave.length > 0 && (
                <span style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--pb-muted)' }}>
                  {pill('var(--pb-border)', 'var(--pb-muted)', String(alreadyHave.length))} already in database
                </span>
              )}
              {preview.modifiedCount > 0 && (
                <span style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--pb-text)' }}>
                  {pill('rgba(99,102,241,0.15)', 'var(--pb-primary)', String(preview.modifiedCount))} to update
                </span>
              )}
              {preview.removedCount > 0 && (
                <span style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--pb-text)' }}>
                  {pill('rgba(239,68,68,0.12)', '#ef4444', String(preview.removedCount))} to remove
                </span>
              )}
            </div>

            {/* Body */}
            <div style={{ flex: '1 1 auto', overflowY: 'auto' }}>
              {error && (
                <div style={{ margin: '1rem 1.5rem 0', padding: '0.6rem 1rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, fontSize: '0.83rem', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className="bi bi-exclamation-triangle" />{error}
                </div>
              )}
              {preview.addedCount === 0 && preview.modifiedCount === 0 && preview.removedCount === 0 ? (
                <div style={{ textAlign: 'center', padding: '3.5rem 1rem', color: 'var(--pb-muted)' }}>
                  <i className="bi bi-check2-circle" style={{ fontSize: '2.5rem', color: '#22c55e' }} />
                  <p style={{ marginTop: '1rem', marginBottom: 0, fontWeight: 500, color: 'var(--pb-text)' }}>Everything is up to date — no new transactions.</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      {['Date', 'Merchant / Description', 'Category', 'Amount', 'Status'].map((h, i) => (
                        <th key={h} style={{
                          fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '0.07em', color: 'var(--pb-muted)',
                          padding: '0.5rem 0.75rem', textAlign: i === 3 ? 'right' : 'left',
                          borderBottom: '2px solid var(--pb-border)', background: 'var(--pb-glass-bg-strong)',
                          position: 'sticky', top: 0, zIndex: 1,
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAdded.map(tx => renderRow(tx,
                      tx.existsInDb
                        ? pill('var(--pb-border)', 'var(--pb-muted)', 'Already saved')
                        : pill('rgba(34,197,94,0.15)', '#16a34a', 'New'),
                      `add-${tx.plaidTransactionId}`))}
                    {preview.modified.map(tx => renderRow(tx,
                      pill('rgba(99,102,241,0.15)', 'var(--pb-primary)', 'Update'),
                      `mod-${tx.plaidTransactionId}`))}
                    {preview.removed.map(id => (
                      <tr key={`rm-${id}`} style={{ background: 'rgba(239,68,68,0.04)', borderBottom: '1px solid var(--pb-border)' }}>
                        <td colSpan={4} style={{ padding: '0.45rem 0.75rem', fontSize: '0.78rem', color: 'var(--pb-muted)', fontFamily: 'monospace' }}>{id}</td>
                        <td style={{ padding: '0.45rem 0.75rem' }}>{pill('rgba(239,68,68,0.12)', '#ef4444', 'Remove')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '0.85rem 1.5rem', borderTop: '1px solid var(--pb-border)', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', flexShrink: 0, borderRadius: '0 0 16px 16px', fontSize: '0.8rem', color: 'var(--pb-muted)' }}>
              <button
                onClick={onClose}
                disabled={saving}
                className="btn btn-sm btn-outline-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="btn btn-sm btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                {saving ? (
                  <><span className="spinner-border spinner-border-sm" style={{ width: '0.8rem', height: '0.8rem', borderWidth: 2 }} />Saving…</>
                ) : hasChanges ? (
                  <>
                    <i className="bi bi-floppy" />
                    Save {newAdded.length > 0 ? `${newAdded.length} new` : ''}
                    {newAdded.length > 0 && preview.modifiedCount > 0 ? ' + ' : ''}
                    {preview.modifiedCount > 0 ? `${preview.modifiedCount} updates` : ''}
                    {preview.removedCount  > 0 ? ` + ${preview.removedCount} removals` : ''}
                  </>
                ) : 'Nothing to save'}
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  )
}

// ── ReAuthButton ───────────────────────────────────────────────────────────────

interface ReAuthButtonProps { itemId: string; onSuccess: () => void }

function ReAuthButton({ itemId, onSuccess }: ReAuthButtonProps) {
  const qc = useQueryClient()
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState<string | null>(null)

  const { open, ready } = usePlaidLink({
    token: linkToken ?? '',
    onSuccess: () => { setLinkToken(null); qc.invalidateQueries({ queryKey: ['plaidItems'] }); onSuccess() },
    onExit:    () => { setLinkToken(null); setLoading(false) },
  })

  if (linkToken && ready) open()

  const handleClick = async () => {
    setError(null); setLoading(true)
    try {
      const result = await createUpdateLinkToken(itemId)
      setLinkToken(result.linkToken)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not start re-authentication.'
      setError(msg); setLoading(false)
    }
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
      <button className="btn btn-sm btn-warning" onClick={handleClick} disabled={loading} style={{ fontWeight: 600 }}>
        {loading
          ? <><span className="spinner-border spinner-border-sm me-1" />Connecting…</>
          : <><i className="bi bi-arrow-repeat me-1" />Re-authenticate</>}
      </button>
      {error && <span style={{ fontSize: '0.78rem', color: 'var(--pb-danger, #ef4444)' }}>{error}</span>}
    </span>
  )
}

// ── KPI helpers ────────────────────────────────────────────────────────────────

function computeKpis(items: PlaidItem[]) {
  const allAccounts = items.flatMap(i => i.accounts)
  const hasBalanceData  = allAccounts.some(a => a.balanceCurrent != null)
  const depository      = allAccounts.filter(a => a.type.toLowerCase() === 'depository')
  const creditLoan      = allAccounts.filter(a => { const t = a.type.toLowerCase(); return t === 'credit' || t === 'loan' })
  const totalCash       = depository.reduce((s, a) => s + (a.balanceCurrent ?? 0), 0)
  const totalCreditOwed = creditLoan.reduce((s, a)  => s + (a.balanceCurrent  ?? 0), 0)
  const creditLimit     = creditLoan.reduce((s, a)  => s + (a.balanceLimit    ?? 0), 0)
  const availableToBorrow = creditLoan.reduce((s, a) => s + (effectiveAvailable(a) ?? 0), 0)
  const utilizationPct  = creditLimit > 0 ? Math.min(100, (totalCreditOwed / creditLimit) * 100) : null
  return { hasBalanceData, totalCash, totalCreditOwed, utilizationPct, availableToBorrow, creditLimit }
}

// ── Right-panel detail ─────────────────────────────────────────────────────────

interface InstitutionDetailProps {
  item:           PlaidItem
  onSaved:        () => void
  onPreviewReady: (itemId: string, data: PlaidPreviewResult, onSaved: (result: SyncResult) => void) => void
}

function InstitutionDetail({ item, onSaved, onPreviewReady }: InstitutionDetailProps) {
  const qc = useQueryClient()
  const navigate = useNavigate()

  // Open the full "Add Transaction" form pre-targeted at this account (and its business).
  const addTransaction = (acc: PlaidAccountInfo) => {
    const params = new URLSearchParams({ account: acc.plaidAccountId, accountName: acc.nickname ?? acc.name })
    if (!acc.isPersonal && acc.businessId != null) params.set('business', String(acc.businessId))
    navigate(`/transactions/new?${params.toString()}`)
  }

  const [previewing,    setPreviewing]    = useState(false)
  const [previewError,  setPreviewError]  = useState<string | null>(null)
  const [loginRequired, setLoginRequired] = useState(false)
  const [resetting,     setResetting]     = useState(false)
  const [syncResult,    setSyncResult]    = useState<SyncResult | null>(null)
  const [chartOffset,      setChartOffset]      = useState(0)
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)

  const handleSaved = async (result: SyncResult) => {
    setSyncResult(result)
    qc.invalidateQueries({ queryKey: ['dashboard'] })
    try { await refreshPlaidBalances(item.itemId) } catch { /* non-blocking */ }
    qc.invalidateQueries({ queryKey: ['plaidItems'] })
    onSaved()
  }

  const handlePull = async (resetCursor = false) => {
    if (resetCursor) {
      setResetting(true)
      try { await resetPlaidCursor(item.itemId) }
      catch { setPreviewError('Could not reset cursor. Please try again.'); setResetting(false); return }
      setResetting(false)
    }
    setPreviewing(true); setPreviewError(null); setLoginRequired(false); setSyncResult(null)
    try {
      const data = await previewPlaidTransactions(item.itemId)
      onPreviewReady(item.itemId, data, handleSaved)
    } catch (err) {
      if (err instanceof ApiError && err.message.includes('ITEM_LOGIN_REQUIRED')) {
        setLoginRequired(true)
        setPreviewError('This bank requires re-authentication before transactions can be fetched.')
      } else {
        setPreviewError('Could not fetch transactions from Plaid. Please try again.')
      }
    } finally { setPreviewing(false) }
  }

  // ── Chart data ───────────────────────────────────────────────────────────────
  // Only count transactions for accounts visible on this page (syncEnabled)
  const visibleAccountIds = useMemo(
    () => new Set(item.accounts.map(a => a.plaidAccountId)),
    [item.accounts],
  )
  const monthlyData = useMemo(
    () => (item.stats?.monthlyTxCounts ?? []).filter(
      m => m.plaidAccountId == null || visibleAccountIds.has(m.plaidAccountId)
    ),
    [item.stats, visibleAccountIds],
  )

  // When an account is selected filter to that account; otherwise aggregate all
  const filteredMonthlyData = useMemo(
    () => selectedAccountId
      ? monthlyData.filter(m => m.plaidAccountId === selectedAccountId)
      : monthlyData,
    [monthlyData, selectedAccountId],
  )

  const chartWindow = useMemo(() => buildChartWindow(chartOffset), [chartOffset])

  // Selected account display name for the chart header
  const selectedAccountName = useMemo(() => {
    if (!selectedAccountId) return null
    const acc = item.accounts.find(a => a.plaidAccountId === selectedAccountId)
    return acc ? (acc.nickname ?? acc.name) : null
  }, [selectedAccountId, item.accounts])

  const chartData = useMemo(() => {
    const counts = chartWindow.map(w => {
      const rows = filteredMonthlyData.filter(m => m.year === w.year && m.month === w.month)
      return rows.reduce((s, m) => s + m.count, 0)
    })
    return {
      labels: chartWindow.map(w => w.label),
      datasets: [{
        data:            counts,
        borderColor:     '#6366f1',
        backgroundColor: 'rgba(99,102,241,0.08)',
        borderWidth:     2,
        pointRadius:     3,
        pointBackgroundColor: '#6366f1',
        fill:            true,
        tension:         0.3,
      }],
    }
  }, [chartWindow, filteredMonthlyData])

  // Earliest data point determines how far back navigation can go
  const earliestEntry = filteredMonthlyData.length > 0
    ? filteredMonthlyData.reduce((a, b) => a.year < b.year || (a.year === b.year && a.month < b.month) ? a : b)
    : null

  const windowStart = chartWindow[0]
  const canGoBack = earliestEntry != null
    && (windowStart.year > earliestEntry.year
        || (windowStart.year === earliestEntry.year && windowStart.month > earliestEntry.month))
  const canGoForward = chartOffset > 0

  const lastSync = item.lastSyncedAt ? new Date(item.lastSyncedAt).toLocaleString() : 'Never'

  const sortedAccounts = useMemo(
    () => [...item.accounts].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [item.accounts]
  )

  return (
    <>
      {/* ── Header ────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>
            <i className="bi bi-bank2 me-2" style={{ color: 'var(--pb-muted)' }} />
            {item.institutionName ?? 'Unknown institution'}
          </div>
          <div className="pb-kpi-label" style={{ marginBottom: 0, marginTop: '0.2rem' }}>
            {item.accounts.length} account{item.accounts.length !== 1 ? 's' : ''} · Last synced: {lastSync}
          </div>
        </div>
        {/* Pull transactions split button — hidden for manual (statement-only) institutions with no Plaid link */}
        {item.institutionId != null && (
        <div className="btn-group btn-group-sm">
          <button
            className="btn btn-outline-primary d-flex align-items-center gap-1"
            onClick={() => handlePull(false)}
            disabled={previewing || resetting}
          >
            {previewing ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-cloud-download" />}
            {previewing ? 'Fetching…' : 'Pull transactions'}
          </button>
          <button
            className="btn btn-outline-secondary"
            title="Reset cursor and re-pull all transactions (last 90 days)"
            onClick={() => handlePull(true)}
            disabled={previewing || resetting}
          >
            {resetting ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-arrow-counterclockwise" />}
          </button>
        </div>
        )}
      </div>

      {/* ── Error / success banners ────────────────────────────────────────────── */}
      {previewError && (
        <div style={{
          fontSize: '0.82rem',
          color: loginRequired ? '#92400e' : 'var(--pb-danger, #ef4444)',
          background: loginRequired ? 'rgba(251,191,36,0.1)' : 'rgba(239,68,68,0.08)',
          border: loginRequired ? '1px solid rgba(251,191,36,0.4)' : '1px solid rgba(239,68,68,0.2)',
          borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
            <i className={loginRequired ? 'bi bi-shield-exclamation' : 'bi bi-exclamation-triangle'} />
            <span>{previewError}</span>
          </div>
          {loginRequired && (
            <div style={{ marginTop: '0.5rem' }}>
              <ReAuthButton itemId={item.itemId} onSuccess={() => { setPreviewError(null); setLoginRequired(false) }} />
            </div>
          )}
        </div>
      )}
      {syncResult && (
        <div style={{
          fontSize: '0.82rem', color: '#16a34a',
          background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)',
          borderRadius: 6, padding: '0.4rem 0.7rem', marginBottom: '0.75rem',
          display: 'flex', alignItems: 'center', gap: '0.4rem',
        }}>
          <i className="bi bi-check-circle" />
          Saved — <strong>{syncResult.added}</strong> new,{' '}
          <strong>{syncResult.modified}</strong> updated,{' '}
          <strong>{syncResult.removed}</strong> removed
        </div>
      )}

      {/* ── Transaction count chart ────────────────────────────────────────────── */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--pb-muted)' }}>
            Transactions per month
            {selectedAccountName && (
              <span style={{ marginLeft: '0.5rem', fontWeight: 500, textTransform: 'none', color: 'var(--pb-primary)' }}>
                — {selectedAccountName}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 2 }}>
            <button
              onClick={() => setChartOffset(o => o + 6)}
              disabled={!canGoBack}
              style={{ background: 'none', border: 'none', padding: '0 4px', cursor: canGoBack ? 'pointer' : 'default', color: canGoBack ? 'var(--pb-text)' : 'var(--pb-border)', lineHeight: 1 }}
            >
              <i className="bi bi-chevron-left" style={{ fontSize: '0.85rem' }} />
            </button>
            <button
              onClick={() => setChartOffset(o => o - 6)}
              disabled={!canGoForward}
              style={{ background: 'none', border: 'none', padding: '0 4px', cursor: canGoForward ? 'pointer' : 'default', color: canGoForward ? 'var(--pb-text)' : 'var(--pb-border)', lineHeight: 1 }}
            >
              <i className="bi bi-chevron-right" style={{ fontSize: '0.85rem' }} />
            </button>
          </div>
        </div>
        <div style={{ height: 150 }}>
          <Line data={chartData} options={CHART_OPTS} />
        </div>
      </div>

      {/* ── Accounts ──────────────────────────────────────────────────────────── */}
      <div>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--pb-muted)', marginBottom: 10 }}>
          Accounts
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--pb-border)' }}>
                <th style={{ width: 28, padding: '0.35rem 0.25rem' }} />
                <th style={{ padding: '0.35rem 0.5rem', fontWeight: 600, color: 'var(--pb-muted)', textAlign: 'left',  fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Account</th>
                <th style={{ padding: '0.35rem 0.5rem', fontWeight: 600, color: 'var(--pb-muted)', textAlign: 'right', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Current</th>
                <th style={{ padding: '0.35rem 0.5rem', fontWeight: 600, color: 'var(--pb-muted)', textAlign: 'right', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Available</th>
                <th style={{ padding: '0.35rem 0.5rem', fontWeight: 600, color: 'var(--pb-muted)', textAlign: 'right', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Limit</th>
                <th style={{ padding: '0.35rem 0.5rem', fontWeight: 600, color: 'var(--pb-muted)', textAlign: 'left',  fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Type</th>
                <th style={{ width: 36, padding: '0.35rem 0.25rem' }} />
              </tr>
            </thead>
            <tbody>
              {sortedAccounts.map((acc, idx) => {
                const accType    = acc.type.toLowerCase()
                const isCredit   = accType === 'credit' || accType === 'loan'
                const utilPct    = isCredit && acc.balanceLimit && acc.balanceCurrent != null
                  ? Math.min(100, (acc.balanceCurrent / acc.balanceLimit) * 100) : null
                const isSelected = acc.plaidAccountId === selectedAccountId

                return (
                  <tr
                    key={acc.plaidAccountId}
                    onClick={() => setSelectedAccountId(id => id === acc.plaidAccountId ? null : acc.plaidAccountId)}
                    style={{
                      borderBottom: idx < sortedAccounts.length - 1 ? '1px solid var(--pb-border)' : undefined,
                      cursor: 'pointer',
                      background: isSelected ? 'rgba(99,102,241,0.06)' : undefined,
                      borderLeft: isSelected ? '3px solid var(--pb-primary)' : '3px solid transparent',
                      transition: 'background 0.1s',
                    }}
                  >
                    {/* Selection indicator */}
                    <td style={{ padding: '0.3rem 0.25rem', verticalAlign: 'middle', width: 20 }}>
                      {isSelected && (
                        <i className="bi bi-check-circle-fill" style={{ color: 'var(--pb-primary)', fontSize: '0.8rem', display: 'block', textAlign: 'center' }} />
                      )}
                    </td>
                    {/* Account name */}
                    <td style={{ padding: '0.55rem 0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                        {typeDot(acc.type)}
                        <span style={{ fontWeight: 500 }}>{acc.nickname ?? acc.name}</span>
                        {acc.mask && <span style={{ color: 'var(--pb-muted)', fontSize: '0.78rem' }}>••••{acc.mask}</span>}
                      </div>
                    </td>
                    {/* Current balance */}
                    <td style={{ padding: '0.55rem 0.5rem', textAlign: 'right', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                      {isCredit && acc.balanceCurrent != null
                        ? <span style={{ color: acc.balanceCurrent > 0 ? 'var(--pb-danger, #ef4444)' : undefined }}>{fmt(acc.balanceCurrent)}</span>
                        : <span>{fmt(acc.balanceCurrent)}</span>}
                    </td>
                    {/* Available */}
                    <td style={{ padding: '0.55rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {(() => { const av = effectiveAvailable(acc); return av != null ? <span style={{ color: '#16a34a' }}>{fmt(av)}</span> : '—' })()}
                    </td>
                    {/* Limit + utilization bar */}
                    <td style={{ padding: '0.55rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {acc.balanceLimit != null ? (
                        <div>
                          <div>{fmt(acc.balanceLimit)}</div>
                          {utilPct != null && (
                            <div style={{ marginTop: 3 }}>
                              <div style={{ height: 3, borderRadius: 2, background: 'var(--pb-border)', width: 60, marginLeft: 'auto' }}>
                                <div style={{ height: '100%', borderRadius: 2, width: `${utilPct}%`, background: utilPct > 80 ? '#ef4444' : utilPct > 50 ? '#f97316' : '#22c55e' }} />
                              </div>
                            </div>
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    {/* Type */}
                    <td style={{ padding: '0.55rem 0.5rem' }}>
                      <span style={{
                        fontSize: '0.72rem', fontWeight: 600, textTransform: 'capitalize',
                        padding: '0.1rem 0.45rem', borderRadius: 4,
                        background: accType === 'credit' ? 'rgba(239,68,68,0.1)' : accType === 'depository' ? 'rgba(34,197,94,0.1)' : accType === 'loan' ? 'rgba(249,115,22,0.1)' : 'rgba(148,163,184,0.1)',
                        color: accType === 'credit' ? '#ef4444' : accType === 'depository' ? '#16a34a' : accType === 'loan' ? '#f97316' : 'var(--pb-muted)',
                      }}>
                        {acc.type}
                      </span>
                    </td>
                    {/* Add transaction to this account */}
                    <td style={{ padding: '0.3rem 0.25rem', textAlign: 'center', verticalAlign: 'middle', width: 36 }}>
                      <button
                        type="button"
                        title={`Add a transaction to ${acc.nickname ?? acc.name}`}
                        onClick={e => { e.stopPropagation(); addTransaction(acc) }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 26, height: 26, borderRadius: 7, cursor: 'pointer',
                          border: '1px solid var(--pb-border)', background: 'transparent', color: 'var(--pb-primary)',
                        }}
                      >
                        <i className="bi bi-plus-lg" style={{ fontSize: '0.8rem' }} />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

    </>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────────

interface AccountsPageProps { isPersonal?: boolean }

export default function AccountsPage({ isPersonal = true }: AccountsPageProps) {
  const qc = useQueryClient()

  const { data: items = [], isLoading, error } = useQuery<PlaidItem[]>({
    queryKey: ['plaidItems'],
    queryFn:  () => getPlaidItems(undefined, true),   // fetch all items, includeStats=true
  })

  // Show only active items that have at least one account matching this module and sync-enabled
  const activeItems = useMemo(
    () => items
      .filter(i => i.isActive)
      .map(i => ({
        ...i,
        accounts: i.accounts.filter(a => a.isPersonal === isPersonal && a.syncEnabled),
      }))
      .filter(i => i.accounts.length > 0),
    [items, isPersonal],
  )

  // Selected institution
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)

  // Page-level preview modal (lifted out of InstitutionDetail so it overlays the full page)
  const [pagePreview, setPagePreview] = useState<{
    itemId: string
    data: PlaidPreviewResult
    onSaved: (result: SyncResult) => void
  } | null>(null)

  // Auto-select first on load
  const selectedItem = activeItems.find(i => i.itemId === selectedItemId)
    ?? (activeItems.length > 0 ? activeItems[0] : null)

  const kpis = computeKpis(activeItems)

  const sortedItems = useMemo(
    () => [...activeItems].sort((a, b) => a.sortOrder - b.sortOrder || a.linkedAt.localeCompare(b.linkedAt)),
    [activeItems]
  )

  return (
    <div>

      {/* Page header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="pb-page-title mb-0">Accounts</h1>
      </div>

      {error && (
        <div className="alert alert-danger mb-4">
          <i className="bi bi-exclamation-triangle me-2" />Failed to load accounts. Please refresh.
        </div>
      )}

      {/* KPI row */}
      {!isLoading && activeItems.length > 0 && kpis.hasBalanceData && (
        <div className="row g-3 mb-4">
          <div className="col-sm-6 col-xl-3">
            <div className="pb-card pb-card-sm h-100">
              <div className="pb-kpi-label mb-1"><i className="bi bi-piggy-bank me-1" />Total Cash</div>
              <div className="pb-kpi-value" style={{ color: '#16a34a' }}>{money(kpis.totalCash)}</div>
              <div className="pb-kpi-label" style={{ marginTop: '0.25rem', marginBottom: 0 }}>Checking &amp; savings</div>
            </div>
          </div>
          <div className="col-sm-6 col-xl-3">
            <div className="pb-card pb-card-sm h-100">
              <div className="pb-kpi-label mb-1"><i className="bi bi-credit-card me-1" />Total Credit</div>
              <div className="pb-kpi-value" style={{ color: kpis.totalCreditOwed > 0 ? 'var(--pb-danger, #ef4444)' : undefined }}>{money(kpis.totalCreditOwed)}</div>
              <div className="pb-kpi-label" style={{ marginTop: '0.25rem', marginBottom: 0 }}>Cards &amp; loans owed</div>
            </div>
          </div>
          <div className="col-sm-6 col-xl-3">
            <div className="pb-card pb-card-sm h-100">
              <div className="pb-kpi-label mb-1"><i className="bi bi-speedometer2 me-1" />Utilization Rate</div>
              {kpis.utilizationPct != null ? (
                <>
                  <div className="pb-kpi-value" style={{ color: kpis.utilizationPct > 80 ? '#ef4444' : kpis.utilizationPct > 50 ? '#f97316' : '#16a34a' }}>
                    {kpis.utilizationPct.toFixed(1)}%
                  </div>
                  <div style={{ marginTop: '0.4rem' }}>
                    <div style={{ height: 4, borderRadius: 2, background: 'var(--pb-border)' }}>
                      <div style={{ height: '100%', borderRadius: 2, width: `${kpis.utilizationPct}%`, background: kpis.utilizationPct > 80 ? '#ef4444' : kpis.utilizationPct > 50 ? '#f97316' : '#22c55e', transition: 'width 0.3s ease' }} />
                    </div>
                  </div>
                  <div className="pb-kpi-label" style={{ marginTop: '0.25rem', marginBottom: 0 }}>of {money(kpis.creditLimit)} limit</div>
                </>
              ) : (
                <div className="pb-kpi-value" style={{ color: 'var(--pb-muted)' }}>—</div>
              )}
            </div>
          </div>
          <div className="col-sm-6 col-xl-3">
            <div className="pb-card pb-card-sm h-100">
              <div className="pb-kpi-label mb-1"><i className="bi bi-wallet2 me-1" />Available to Borrow</div>
              <div className="pb-kpi-value" style={{ color: '#16a34a' }}>{money(kpis.availableToBorrow)}</div>
              <div className="pb-kpi-label" style={{ marginTop: '0.25rem', marginBottom: 0 }}>Remaining credit</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div className="text-center py-5"><div className="spinner-border" /></div>
      ) : activeItems.length === 0 ? (
        <div className="pb-card" style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--pb-muted)' }}>
          <i className="bi bi-bank2" style={{ fontSize: '3rem', opacity: 0.3 }} />
          <p style={{ marginTop: '1rem', marginBottom: '0.25rem', fontWeight: 500 }}>No accounts for this module.</p>
          <p style={{ fontSize: '0.85rem', margin: 0, opacity: 0.7 }}>Go to Settings → Financial Institutions to connect or configure accounts.</p>
        </div>
      ) : (

        /* ── Two-column layout ── */
        <div className="row g-4 mb-4">

          { /* ── Left: institution list ── */}
          <div className="col-lg-4">
            <div className="pb-card" style={{ padding: 0 }}>
              <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--pb-border)' }}>
                <span className="pb-kpi-label"><i className="bi bi-bank2 me-1" />Financial Institutions</span>
              </div>

              {sortedItems.map((item, idx) => {
                const isSelected = (selectedItem?.itemId ?? sortedItems[0]?.itemId) === item.itemId
                const stats      = item.stats

                return (
                  <div
                    key={item.itemId}
                    onClick={() => setSelectedItemId(item.itemId)}
                    style={{
                      padding: '0.7rem 1rem',
                      borderBottom: idx < sortedItems.length - 1 ? '1px solid var(--pb-border)' : undefined,
                      cursor: 'pointer',
                      background: isSelected ? 'rgba(99,102,241,0.06)' : undefined,
                      borderLeft: isSelected ? '3px solid var(--pb-primary)' : '3px solid transparent',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.03)' }}
                    onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = '' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                      {/* Institution info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: isSelected ? 'var(--pb-primary)' : 'var(--pb-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.institutionName ?? 'Unknown institution'}
                          {isSelected && <i className="bi bi-check-circle-fill ms-1" style={{ color: 'var(--pb-primary)', fontSize: '0.72rem' }} />}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--pb-muted)', marginTop: 2 }}>
                          {item.accounts.length} account{item.accounts.length !== 1 ? 's' : ''}
                          {stats != null && <> · {stats.transactionCount.toLocaleString()} txns</>}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--pb-muted)', marginTop: 3 }}>
                          <i className="bi bi-clock me-1" style={{ fontSize: '0.68rem' }} />
                          {item.lastSyncedAt ? (() => {
                            const d    = new Date(item.lastSyncedAt)
                            const days = Math.floor((Date.now() - d.getTime()) / 86_400_000)
                            const ago  = days === 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`
                            return <>{ago} · {d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</>
                          })() : 'Never synced'}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* ── Right: detail panel ── */}
          <div className="col-lg-8">
            <div className="pb-card">
              {selectedItem ? (
                <InstitutionDetail
                  key={selectedItem.itemId}
                  item={selectedItem}
                  onSaved={() => qc.invalidateQueries({ queryKey: ['plaidItems'] })}
                  onPreviewReady={(itemId, data, onSaved) => setPagePreview({ itemId, data, onSaved })}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 180, color: 'var(--pb-muted)' }}>
                  <i className="bi bi-bank2" style={{ fontSize: '2rem', opacity: 0.3, marginBottom: '0.75rem' }} />
                  <div style={{ fontSize: '0.88rem' }}>Select an institution to view details</div>
                </div>
              )}
            </div>
          </div>

        </div>
      )}

      { /* ── Full-page preview modal (covers entire accounts page) ── */}
      {pagePreview && (
        <PlaidPreviewModal
          itemId={pagePreview.itemId}
          preview={pagePreview.data}
          onClose={() => setPagePreview(null)}
          onSaved={result => { setPagePreview(null); pagePreview.onSaved(result) }}
        />
      )}

    </div>
  )
}
